import React, { useEffect, useState } from "react";
import DeferredFeedback from "./DeferredFeedback";
import { formatCheckoutTime } from "../domain/timeSummary";

export const formatPickupBranchAddress = (branch = {}, arabic = false, baseCountry = "", compact = false) => {
  const area = (arabic && branch.ar_area) || branch.area;
  const fields = [
    ["zone", "Zone", "منطقة"],
    ["block", "Block", baseCountry === "Bahrain" ? "مجمع" : "قطعة"],
    ["street", "Street", "شارع"],
    ["avenue", "Avenue", "جادة"],
    ["building", "Building", "مبنى"],
    ["floor", "Floor", "طابق"],
    ["apartment", "Apartment", "شقة"],
  ];
  const address = !compact && ((arabic && branch.ar_address) || branch.address);
  return [typeof area === "string" ? area : "", ...(address ? [address] : fields
    .filter(([key]) => compact ? ["street", "building"].includes(key) : key === "zone" ? baseCountry === "Qatar" : key === "block" ? baseCountry !== "Qatar" : true)
    .filter(([key]) => branch[key] !== null && branch[key] !== undefined && `${branch[key]}`.trim())
    .map(([key, en, ar]) => `${arabic ? ar : en} ${branch[key]}`))]
    .filter(Boolean).join(arabic ? "، " : ", ");
};

export const pickupHours = (slots = []) => {
  const days = new Map();
  slots.forEach((slot) => {
    const day = Number(slot.day);
    if (!Number.isInteger(day) || day < 0 || day > 6 || !formatCheckoutTime(slot.start) || !formatCheckoutTime(slot.end)) return;
    if (!days.has(day)) days.set(day, new Map());
    days.get(day).set(`${slot.start}-${slot.end}`, { start: slot.start, end: slot.end });
  });
  return [...days].sort(([a], [b]) => a - b).map(([day, periods]) => ({
    day, periods: [...periods.values()].sort((a, b) => a.start.localeCompare(b.start)),
  }));
};

export default function ContextBranchDetails({ branch, language, baseCountry, loadDetails }) {
  const arabic = language === "arabic";
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);
  const [retry, setRetry] = useState(0);
  useEffect(() => {
    let active = true;
    if (branch.loaded_details !== false || !loadDetails) {
      setLoading(false);
      setFailed(false);
      return () => { active = false; };
    }
    setLoading(true);
    setFailed(false);
    Promise.resolve().then(() => loadDetails(branch.id)).then((result) => {
      if (active) { setLoading(false); setFailed(result === false); }
    }, () => {
      if (active) { setLoading(false); setFailed(true); }
    });
    return () => { active = false; };
  }, [branch.id, branch.loaded_details, loadDetails, retry]);

  const address = formatPickupBranchAddress(branch, arabic, baseCountry);
  const hours = pickupHours(branch.slots || []);
  const weekdays = arabic
    ? ["الاثنين", "الثلاثاء", "الأربعاء", "الخميس", "الجمعة", "السبت", "الأحد"]
    : ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
  const phone = /^\+?[\d\s()-]+$/.test(branch.contact_number || "") ? branch.contact_number : "";
  const lat = Number(branch.lat), lng = Number(branch.lng);
  const hasMap = branch.lat !== null && branch.lat !== undefined && branch.lat !== "" &&
    branch.lng !== null && branch.lng !== undefined && branch.lng !== "" &&
    Number.isFinite(lat) && Number.isFinite(lng) && Math.abs(lat) <= 90 && Math.abs(lng) <= 180;
  return (
    <div className="context-sheet__branch-info" id="context-branch-details">
      {address && <p>{address}</p>}
      {branch.additional && <p>{branch.additional}</p>}
      {(phone || hasMap) && <div className="context-sheet__branch-links">
        {phone && <a href={`tel:${phone}`}>{arabic ? "اتصل بالفرع" : "Call branch"}</a>}
        {hasMap && <a href={`https://maps.google.com/maps?q=${lat},${lng}`} target="_blank" rel="noopener noreferrer">{arabic ? "الاتجاهات" : "Get directions"}</a>}
      </div>}
      {hours.length > 0 && <details className="context-sheet__branch-hours-disclosure">
        <summary>{arabic ? "ساعات الاستلام" : "Pickup hours"}</summary>
        <dl className="context-sheet__branch-hours">{hours.map(({ day, periods }) =>
          <div key={day}><dt>{weekdays[day]}</dt><dd>{periods.map((period) =>
            <span key={`${period.start}-${period.end}`}>{formatCheckoutTime(period.start, arabic)} – {formatCheckoutTime(period.end, arabic)}</span>
          )}</dd></div>
        )}</dl>
      </details>}
      <DeferredFeedback active={loading}><p role="status">{arabic ? "جاري عرض تفاصيل الفرع…" : "Getting branch details…"}</p></DeferredFeedback>
      {failed && <p role="status">{arabic ? "تعذر عرض بعض التفاصيل." : "Some details couldn’t be loaded."} <button type="button" onClick={() => setRetry(retry + 1)}>{arabic ? "حاول مجدداً" : "Try again"}</button></p>}
      {!loading && !failed && !address && !hours.length && !phone && !hasMap && <p>{arabic ? "لا توجد تفاصيل إضافية لهذا الفرع." : "No additional details for this branch."}</p>}
    </div>
  );
}
